type Child = Node | string | null | undefined | false;
type Attrs = Record<string, string | number | boolean | EventListener | null | undefined>;

/**
 * Builds an element. Text always goes in as a text node, never as HTML, so a
 * record entry can hold any characters without becoming markup.
 */
export function h<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Attrs = {},
  ...children: Child[]
): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  for (const [name, value] of Object.entries(attrs)) {
    if (value === null || value === undefined || value === false) continue;
    if (typeof value === "function") el.addEventListener(name.replace(/^on/, ""), value);
    else if (name === "class") el.className = String(value);
    // Through the CSSOM, not the attribute: the page's Content Security Policy forbids inline style attributes.
    else if (name === "style") el.style.cssText = String(value);
    else if (name === "value" && "value" in el) (el as HTMLInputElement).value = String(value);
    else if (name === "checked" && el instanceof HTMLInputElement) el.checked = value === true;
    else el.setAttribute(name, value === true ? "" : String(value));
  }
  append(el, children);
  return el;
}

export function append(parent: Node, children: Child[]): void {
  for (const child of children) {
    if (child === null || child === undefined || child === false) continue;
    parent.appendChild(typeof child === "string" ? document.createTextNode(child) : child);
  }
}

export function replace(parent: Element, ...children: Child[]): void {
  parent.replaceChildren();
  append(parent, children);
}

export function byId<T extends HTMLElement = HTMLElement>(id: string): T {
  const el = document.getElementById(id);
  if (!el) throw new Error(`Missing element #${id}`);
  return el as T;
}

const TOAST_MS = 2400;
const TOAST_WITH_ACTION_MS = 5000;
const TOAST_LEAVE_MS = 140;

export interface ToastAction {
  label: string;
  run: () => void;
}

let currentToast: HTMLElement | null = null;
let toastTimer = 0;

function dismissToast(): void {
  const el = currentToast;
  if (!el) return;
  currentToast = null;
  el.classList.add("leaving");
  setTimeout(() => el.remove(), TOAST_LEAVE_MS);
}

/** One note at a time. With an action, it stays long enough to be tapped. Undo lives here, not in a dialog. */
export function toast(message: string, action?: ToastAction): void {
  currentToast?.remove();
  clearTimeout(toastTimer);
  const el = h(
    "div",
    { class: "toast noprint", role: "status" },
    h("span", {}, message),
    action &&
      h(
        "button",
        {
          type: "button",
          onclick: () => {
            action.run();
            dismissToast();
          },
        },
        action.label,
      ),
  );
  document.body.appendChild(el);
  currentToast = el;
  toastTimer = window.setTimeout(dismissToast, action ? TOAST_WITH_ACTION_MS : TOAST_MS);
}

/** How long a pressed control keeps its "Saved" label before the resting label returns. */
export const ACK_MS = 700;

/** Puts a short confirmation on the control that was pressed, then restores its label. */
export function acknowledge(control: HTMLElement, word = "Saved"): void {
  const original = control.dataset.idleLabel ?? control.textContent ?? "";
  control.dataset.idleLabel = original;
  window.clearTimeout(Number(control.dataset.ackTimer));
  control.textContent = word;
  control.dataset.ackTimer = String(
    window.setTimeout(() => {
      control.textContent = control.dataset.idleLabel ?? original;
      delete control.dataset.ackTimer;
    }, ACK_MS),
  );
}

/**
 * Asks in the page, beside the control, and resolves true only when the person confirms.
 * A second call while the question is open resolves false.
 */
export function confirmInline(host: HTMLElement, message: string, confirmLabel: string): Promise<boolean> {
  if (host.dataset.open === "1") return Promise.resolve(false);
  host.dataset.open = "1";
  host.hidden = false;
  return new Promise((resolve) => {
    let settled = false;
    const finish = (ok: boolean) => {
      if (settled) return;
      settled = true;
      delete host.dataset.open;
      host.hidden = true;
      replace(host);
      resolve(ok);
    };
    replace(
      host,
      h("p", {}, message),
      h(
        "div",
        { class: "actions" },
        h("button", { class: "btn btn-danger", type: "button", onclick: () => finish(true) }, confirmLabel),
        h("button", { class: "btn btn-ghost", type: "button", onclick: () => finish(false) }, "Cancel"),
      ),
    );
  });
}

export function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : "That did not work. Try again.";
}

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export function longDate(ts: number): string {
  const d = new Date(ts);
  return `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

export function clockTime(ts: number): string {
  const d = new Date(ts);
  const hours = d.getHours() % 12 || 12;
  return `${hours}:${String(d.getMinutes()).padStart(2, "0")}${d.getHours() >= 12 ? "pm" : "am"}`;
}

export function dayLabel(ts: number, now: number): string {
  const day = new Date(ts).toDateString();
  if (day === new Date(now).toDateString()) return `Today, ${longDate(ts)}`;
  if (day === new Date(now - 86_400_000).toDateString()) return `Yesterday, ${longDate(ts)}`;
  return longDate(ts);
}

export function ago(ts: number | null, now: number): string {
  if (ts === null) return "never";
  const days = Math.floor((now - ts) / 86_400_000);
  if (days <= 0) return "today";
  return days === 1 ? "yesterday" : `${days} days ago`;
}

export function elapsed(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(total / 60);
  return `${String(minutes).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
}
